import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import adminActions from '../../../../actions/adminActions';
import { Box, Container, Grid, Paper, Typography } from '@mui/material';
import { useState } from 'react';
import ModalSpinner from '../../../../modules/components/ModalSpinner';
import UserInfo from './UserInfo';
import AssignRoles from './AssignRoles';
import AssignClaims from './AssignClaims';
import Assignments from '../Assignments/Assignments';
import AppAppBar from '../../../../modules/views/AppAppBar';
import { useTheme } from '@emotion/react';

export function hasSelectedRole(checkedRolesState) {
  // eslint-disable-next-line no-unused-vars
  for (const [key, value] of Object.entries(checkedRolesState)) {
    if (value === true) {
      return true;
    }
  }
  return false;
}

export function getReviewGroupTerritories(reviewGroups, roles, userRolesIds, territories) {
  const validTerritories = [];
  userRolesIds.forEach((id) => {
    const role = roles.find((r) => r.id === id);
    const matchingReviewGroup = reviewGroups.find((rg) => rg.value === role.value);
    if (matchingReviewGroup !== undefined && matchingReviewGroup.territoryTypeId !== null) {
      const matchingTerritories = territories.filter(
        (t) => t.territoryTypeId === matchingReviewGroup.territoryTypeId
      );
      validTerritories.push({
        territories: matchingTerritories,
        reviewGroupName: matchingReviewGroup.name,
        reviewGroupValue: matchingReviewGroup.value,
      });
    }
  });
  return validTerritories;
}

function removeAssignments(selectedRoleIds, user, luRoles, luReviewGroups, dispatch) {
  const removedRoles = user.userRoles
    .filter((ur) => !selectedRoleIds.includes(ur.roleId))
    .map((ur) => {
      return luRoles.find((r) => r.id === ur.roleId);
    });
  removedRoles.forEach((role) => {
    if (role.value === 'ENGINEERING' || role.value === 'RAILROAD') {
      const reviewGroup = luReviewGroups.find((rg) => rg.value === role.value);
      const territories = user.assignedTerritories.filter(
        (t) => t.territoryTypeId === reviewGroup.territoryTypeId
      );
      dispatch(
        adminActions.unassignTerritories(
          user.id,
          territories.map((t) => t.id)
        )
      );
    } else if (role.value === 'PERMITMANAGER') {
      dispatch(adminActions.unassignPermitTypeLocations(user.id, user.assignedPermitTypeLocations));
    }
  });
}

export default function EditUser({ match, location }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    users,
    gettingUsers,
    gettingRoles,
    gettingClaims,
    gettingTerritories,
    gettingTerritoryTypes,
  } = useSelector((state) => state.admin);

  const user = users.find((u) => u.id === match.params.id);

  React.useEffect(() => {
    if (!user) {
      dispatch(adminActions.getUsers());
      dispatch(adminActions.getRoles());
      dispatch(adminActions.getClaims());
      dispatch(adminActions.getTerritories());
      dispatch(adminActions.getTerritoryTypes());
    }
  }, [users, match.params.id, dispatch, user]);

  if (
    gettingUsers ||
    gettingRoles ||
    gettingClaims ||
    gettingTerritories ||
    gettingTerritoryTypes
  ) {
    return <ModalSpinner open={true} />;
  }

  if (!user && users.length === 0) {
    return <ModalSpinner open={gettingUsers} />;
  } else if (!user) {
    return <Typography variant="h6">User not found</Typography>;
  }

  return (
    <>
      <AppAppBar pathname={location.pathname} />
      <Container
        component={'main'}
        maxWidth="xl"
        sx={{ flex: 1, padding: theme.spacing(3, 3), background: '#eaeff1' }}
      >
        <Paper sx={{ margin: 'auto', overflow: 'hidden' }}>
          <EditUserContainer user={user} />
        </Paper>
      </Container>
    </>
  );
}

EditUser.propTypes = {
  match: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

function EditUserContainer({ user }) {
  const dispatch = useDispatch();
  const {
    roles,
    claims,
    territories,
    gettingUserAssignedTerritories,
    gettingUserAssignedPermitTypeLocations,
    savingUserEdits,
  } = useSelector((state) => state.admin);
  const { permitTypes, reviewGroups } = useSelector((state) => state.common.lookups);

  const [contractorId, setContractorId] = React.useState(user.contractorId || '');

  React.useEffect(() => {
    dispatch(adminActions.getUserAssignedTerritories(user.id));
    dispatch(adminActions.getUserAssignedPermitTypeLocations(user.id));
  }, [user.id, dispatch]);

  // Initialize roles checked state
  const rolesCheckedInitialState = {};
  roles.forEach((r) => {
    const matchingUserRole = user.userRoles.findIndex((userRole) => userRole.roleId === r.id);
    rolesCheckedInitialState[r.value] = matchingUserRole === -1 ? false : true;
  });
  const [rolesCheckedState, setRolesCheckedState] = useState(rolesCheckedInitialState);
  const [assignableTerritories, setAssignableTerritories] = useState([]);
  React.useEffect(() => {
    const userRoleIds = user.userRoles.map((ur) => ur.roleId);
    const assignableTerritories = getReviewGroupTerritories(
      reviewGroups,
      roles,
      userRoleIds,
      territories
    );
    setAssignableTerritories(assignableTerritories);
  }, [user.userRoles, reviewGroups, territories, roles]);

  // Initialize claims checked state
  const claimsCheckedInitialState = {};
  claims.forEach((c) => {
    const matchingUserClaim = user.userClaims.findIndex((userClaim) => userClaim.claimId === c.id);
    claimsCheckedInitialState[c.name] = matchingUserClaim === -1 ? false : true;
  });
  const [claimsCheckedState, setClaimsCheckedState] = useState(claimsCheckedInitialState);

  return (
    <Container>
      <ModalSpinner
        open={
          gettingUserAssignedTerritories ||
          gettingUserAssignedPermitTypeLocations ||
          savingUserEdits
        }
      />
      <Box sx={{ paddingTop: '20px' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <UserInfo user={user} contractorId={contractorId} setContractorId={setContractorId} />
          </Grid>
          <Grid item xs={12} md={6}>
            <AssignRoles
              roles={roles}
              rolesCheckedState={rolesCheckedState}
              setRolesCheckedState={setRolesCheckedState}
              claimsCheckedState={claimsCheckedState}
              setClaimsCheckedState={setClaimsCheckedState}
            />

            {rolesCheckedState['APPLICANT'] === false && hasSelectedRole(rolesCheckedState) && (
              <AssignClaims
                claims={claims}
                setClaimsCheckedState={setClaimsCheckedState}
                claimsCheckedState={claimsCheckedState}
              />
            )}
          </Grid>
        </Grid>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '20px',
          marginBottom: '20px',
        }}
      >
        <Button
          autoFocus
          variant="contained"
          color="primary"
          onClick={(e) => {
            if (hasSelectedRole(rolesCheckedState) === false) {
              e.preventDefault();
              alert('Please assign at least one role');
              return;
            }
            const roleIds = roles
              .filter((r) => rolesCheckedState[r.value] === true)
              .map((r) => r.id);
            const claimIds = claims
              .filter((c) => claimsCheckedState[c.name] === true)
              .map((c) => c.id);
            dispatch(adminActions.saveUserEdits(user.id, contractorId, roleIds, claimIds));
            removeAssignments(roleIds, user, roles, reviewGroups, dispatch);
          }}
        >
          Save
        </Button>
      </Box>
      {assignableTerritories.length > 0 && (
        <Box sx={{ padding: '10px 0' }}>
          <Assignments
            reviewGroupTerritories={assignableTerritories}
            permitTypes={permitTypes}
            user={user}
          />
        </Box>
      )}
    </Container>
  );
}

EditUserContainer.propTypes = {
  user: PropTypes.object.isRequired,
};
